import { useCallback, useRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import styles from './Tabs.module.scss';

// ============================================
// TYPES
// ============================================

/** Individual tab item configuration. */
export interface TabItem {
  /** Unique identifier used to track the active tab. */
  id: string;
  /** Display label for the tab. Use `t()` from `useTranslation` for localized strings. */
  label: string;
  /** Optional icon rendered after the label (e.g., info tooltip trigger). */
  icon?: ReactNode;
  /** When true, the tab is visually muted and non-interactive. */
  disabled?: boolean;
}

/** Props for the Tabs component. */
export interface TabsProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
  /** Array of tab items to render. */
  tabs: TabItem[];
  /** The `id` of the currently active tab (controlled). */
  activeTab: string;
  /** Callback fired when a tab is selected. Receives the tab `id`. */
  onTabChange: (tabId: string) => void;
  /** When true, tabs stretch to fill the full container width. */
  fullWidth?: boolean;
  /**
   * Optional count badges keyed by tab `id`. When a tab's id exists in
   * this record the numeric value is rendered as a small pill beside the label.
   * Counts of `0` are displayed. Only `undefined` keys are hidden.
   */
  counts?: Record<string, number>;
}

// ============================================
// COMPONENT
// ============================================

/**
 * Horizontal tab bar for switching between content sections.
 *
 * Fully controlled via `activeTab` + `onTabChange`. Supports keyboard
 * navigation (Arrow Left/Right), disabled tabs, and an optional icon per tab.
 *
 * @example
 * ```tsx
 * <Tabs
 *   tabs={[{ id: 'expense', label: 'Employee Expense' }, { id: 'imprest', label: 'Imprest Expense' }]}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   counts={{ expense: 12, imprest: 0 }}
 * />
 * ```
 */
function Tabs({
  tabs,
  activeTab,
  onTabChange,
  fullWidth = false,
  counts,
  className,
  ...rest
}: TabsProps) {
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const enabledTabs = tabs.filter((t) => !t.disabled);
      if (enabledTabs.length === 0) return;

      const currentIndex = enabledTabs.findIndex((t) => t.id === activeTab);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextIndex = currentIndex < enabledTabs.length - 1 ? currentIndex + 1 : 0;
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : enabledTabs.length - 1;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = enabledTabs.length - 1;
      } else {
        return;
      }

      const nextTab = enabledTabs[nextIndex];
      if (nextTab) {
        onTabChange(nextTab.id);

        // Focus the newly active tab button
        const tabList = tabListRef.current;
        if (tabList) {
          const buttons = tabList.querySelectorAll<HTMLButtonElement>(
            `[role="tab"]:not([disabled])`,
          );
          buttons[nextIndex]?.focus();
        }
      }
    },
    [tabs, activeTab, onTabChange],
  );

  const rootClasses = [styles.root, className].filter(Boolean).join(' ');

  return (
    <div className={rootClasses} {...rest}>
      <div
        ref={tabListRef}
        role="tablist"
        aria-orientation="horizontal"
        className={fullWidth ? `${styles.tabList} ${styles.fullWidth}` : styles.tabList}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const isDisabled = tab.disabled === true;

          const tabClasses = [
            styles.tab,
            isActive && styles.active,
            isDisabled && styles.disabled,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              aria-disabled={isDisabled || undefined}
              disabled={isDisabled}
              tabIndex={isActive ? 0 : -1}
              className={tabClasses}
              onClick={() => {
                if (!isDisabled) {
                  onTabChange(tab.id);
                }
              }}
            >
              <span className={styles.label}>{tab.label}</span>
              {counts !== undefined && counts[tab.id] !== undefined && (
                <span
                  className={[
                    styles.count,
                    isActive && styles.countActive,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-label={`${counts[tab.id]}`}
                >
                  {counts[tab.id]}
                </span>
              )}
              {tab.icon && <span className={styles.icon}>{tab.icon}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Tabs;
